import Bold from '@tiptap/extension-bold';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor, JSONContent } from '@tiptap/react';
import { SuggestionProps } from '@tiptap/suggestion';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useRef, forwardRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';

import {
  Flex,
  Text as BokehText,
  InputGroup,
  InputRightElement,
  VisuallyHidden,
} from '@vimeo/bokeh';
import { Send } from '@vimeo/bokeh-icons';

import { T } from '../../translations';
import { RichTextAreaPropsType } from '../../types';
import { findSuggestionMatch } from '../Mention/findSuggestionMatch';
import MentionExtension from '../Mention/MentionExtension';
import { PlaceholderAfterIgnoredNodes } from './PlaceholderAfterIgnoredNodes/PlaceholderAfterIgnoredNodes';
import { TimestampNode } from './TimestampNode/TimestampNode';

export const RichTextArea = memo(
  forwardRef(
    (
      {
        autoFocus = false,
        richtext = '',
        isExpanded = false,
        placeholder,
        isPublic,
        onFocus,
        setCommentText,
        mentionExtensionItemCallback,
        onMentionStart,
        onMentionUpdate,
        onMentionsExit,
        onKeyUpHandler,
        onKeyDownHandler,
        onMentioneeAdd: onMentionAdd,
        onMentioneeRemove: onMentionRemove,
        onSubmit: onSubmitCallbacks,
        onSubmitComment,
        customRichTextAreaStyles,
        teamOwnerId,
        commentContextFields,
        isDisabled = false,
        annotationTimeCode,
        textLimit,
      }: RichTextAreaPropsType,
      ref,
    ) => {
      const mentionPropsRef = useRef<Partial<SuggestionProps> | undefined>(undefined);
      const mentionQueryRef = useRef('');
      const stopFocusRef = useRef(false);
      const sourceFilter = isPublic ? 'viewer' : 'collaborator';

      // Use a ref to ensure the latest onSubmitComment callback is always accessible.
      // This is essential for the onKeyDownHandler in useEditor, which needs to call the most up-to-date callback.
      const onSubmitCommentRef = useRef(onSubmitComment);
      useEffect(() => {
        onSubmitCommentRef.current = onSubmitComment;
      }, [onSubmitComment]);

      const richContent = useMemo(() => {
        try {
          return JSON.parse(richtext);
        } catch (_e) {
          return;
        }
      }, [richtext]);

      const editor = useEditor({
        editorProps: {
          handleDOMEvents: {
            keyup: (view, event) => onKeyUpHandler?.(mentionQueryRef.current, view, event),
            keydown: (view, event) =>
              onKeyDownHandler?.(mentionQueryRef.current, view, event, onSubmitCommentRef.current),
          },
          handleClick: (editorView) => {
            if (!editorView.hasFocus() && !isDisabled) onFocus();
          },
        },
        editable: !isDisabled,
        extensions: [
          Document,
          Bold,
          Text,
          Paragraph,
          HardBreak,
          TimestampNode,
          MentionExtension.configure({
            onMentionAdd,
            onMentionRemove,
            suggestion: {
              allowedPrefixes: null,
              allowSpaces: false,
              findSuggestionMatch,
              items: ({ query }) => {
                if (query.length) {
                  setTimeout(() =>
                    mentionExtensionItemCallback?.(query, sourceFilter, teamOwnerId),
                  );
                }
                mentionQueryRef.current = query;

                return [];
              },
              render: () => ({
                onStart: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  stopFocusRef.current = true;
                  onMentionStart?.();
                },
                onUpdate: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  onMentionUpdate?.();
                },
                onExit: (suggestionProps) => {
                  mentionPropsRef.current = suggestionProps;
                  stopFocusRef.current = false;
                  onMentionsExit?.();
                },
              }),
            },
          }),
          PlaceholderAfterIgnoredNodes.configure({
            placeholder,
            ignoreNodeTypes: ['timestamp'],
          }),
        ],
        onFocus: () => {
          onFocus();
        },
        onUpdate: ({ editor }) => {
          const richText = editor.getJSON();
          // Remove timestamp nodes from the JSON before saving
          const cleanedRichText = removeTimestampNodes(richText);
          setCommentText(JSON.stringify(cleanedRichText || ''));
        },
        content: richContent,
      });

      useEffect(() => {
        if (autoFocus) editor?.commands?.focus?.('end');
      }, [editor, autoFocus]);

      // Sync timestamp when editor or annotationTimeCode changes
      useEffect(() => {
        if (!editor || editor.isDestroyed) {
          return;
        }
        // Use the custom command defined in TimestampNode
        editor.commands.setTimestamp(annotationTimeCode || null);
      }, [editor, annotationTimeCode]);

      useImperativeHandle(ref, () => {
        return {
          clearContent: () => {
            setTimeout(() => {
              editor?.commands?.clearContent();
            }, 0);
          },
          addMentionNode: (props: {
            email?: string;
            name?: string;
            userId?: number;
            teamUserId?: number;
            guestUserId?: string;
          }) => {
            return mentionPropsRef.current?.command?.(props);
          },
          selectRange: () => {
            return editor?.commands?.selectTextblockStart();
          },
          getMentionQuery: () => {
            return mentionQueryRef.current;
          },
          toggleParagraph: () => {
            return editor?.commands?.setParagraph();
          },
          toggleBold: () => {
            return editor?.commands?.toggleBold?.();
          },
          onSubmit: async () => {
            return await onSubmitCallbacks?.();
          },
          isUserMentioned: () => {
            let value = false;
            editor?.state.doc.descendants((node) => {
              if (node.type.name == 'mention') {
                value = true;
              }
            });
            return value;
          },
          commentLength: () => {
            return editor?.getText().trim().length;
          },
          focusEnd: () => {
            if (!stopFocusRef?.current) return editor?.commands?.focus?.('end');
            return false;
          },
          removeFocus: () => {
            if (!stopFocusRef?.current) return editor?.commands?.blur();
          },
          getCommentContent: () => {
            const currentText = editor?.getJSON();
            // Remove timestamp nodes from the JSON before returning
            const cleanedText = removeTimestampNodes(currentText);
            return JSON.stringify(cleanedText || '');
          },
          setContent: (content: string) => {
            return editor?.commands?.setContent(content);
          },
        };
      }, [editor, onSubmitCallbacks]);

      const [localOverflowY, setLocalOverflowY] = useState<'auto' | 'hidden'>('hidden');

      useEffect(() => {
        if (isExpanded) {
          // setting overflowY to auto after a delay to avoid vertical scroll during transition
          setTimeout(() => {
            setLocalOverflowY('auto');
          }, 300);
        } else {
          setLocalOverflowY('hidden');
        }
      }, [isExpanded]);

      const isReplyOrEdit = commentContextFields?.isReply || commentContextFields?.isEdit;
      const commentTextLength = editor?.getText().trim().length || 0;
      const isTextOverLimit = textLimit ? commentTextLength > textLimit : false;

      const defaultRichTextAreaStyles = useMemo(() => {
        let primaryBorderColor = isTextOverLimit ? 'status-destructive-primary' : 'text-primary';
        return {
          '.richtextCommentBox': {
            border: isReplyOrEdit && !isExpanded ? 'none' : '1px solid',
            borderRadius: 'input-md',
            borderColor: isDisabled
              ? 'input-stroke'
              : isExpanded
                ? primaryBorderColor
                : 'input-stroke-hover',
            overflowY: 'hidden',
          },
          '.richtextCommentBox:focus-within': {
            borderWidth: isExpanded ? '2px' : '1px',
            borderColor: isExpanded ? primaryBorderColor : 'input-stroke-hover',
          },
          '.ProseMirror:focus-visible': {
            outline: 'none', // default its blue when none is not specified
          },
          '.ProseMirror': {
            color: 'text-primary',
            height: isExpanded ? `78px` : `38px`,
            padding: isExpanded ? '10px 12px' : '5px 12px',
            fontWeight: 'regular',
            resize: 'none',
            backgroundColor: isExpanded ? 'surface' : 'default',
            transition: 'height 120ms ease-in-out 0s',
            overflowY: localOverflowY,
            'p:first-of-type': {
              // this is to allow space for Send icon when not expanded
              maxWidth: isExpanded ? '100%' : 'calc(100% - 20px)',
            },
          },
        };
      }, [isExpanded, isDisabled, localOverflowY, isReplyOrEdit, isTextOverLimit]);

      return (
        <AnimatePresence>
          <InputGroup
            display="inline-block"
            sx={customRichTextAreaStyles || defaultRichTextAreaStyles}
            as={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto', transition: { type: 'tween' } }}
            exit={{ opacity: 0, height: 0 }}
          >
            <EditorContent className="richtextCommentBox" content={richtext} editor={editor} />
            {!isExpanded ? (
              <InputRightElement opacity="0.6" color="text-primary">
                <Send color={isDisabled ? 'input-stroke-hover' : 'text-primary'} />
              </InputRightElement>
            ) : null}
          </InputGroup>
          <Flex justifyContent="flex-end">
            {isExpanded && textLimit && isTextOverLimit && (
              <BokehText variant="body-sm" color="status-destructive-primary">
                {commentTextLength} / {textLimit}{' '}
                <VisuallyHidden>
                  {T.CharactersOverLimit(Math.abs(textLimit - commentTextLength))}
                </VisuallyHidden>
              </BokehText>
            )}
          </Flex>
        </AnimatePresence>
      );
    },
  ),
);

/**
 * Recursively removes all timestamp nodes from TipTap JSON content.
 *
 * Why this is needed:
 * - Timestamp nodes are purely UI elements controlled by React props (annotationTimeCode)
 * - They should NOT be persisted to the backend or included in saved comment data
 * - ProseMirror/TipTap doesn't provide a clean way to exclude nodes from JSON serialization
 * - This filter ensures timestamps exist only in the editor's internal document, not in output
 *
 * Flow:
 * 1. External richtext (without timestamps) loads into editor
 * 2. Timestamp added internally via setTimestamp command based on annotationTimeCode prop
 * 3. User types/edits content
 * 4. On save/update, this function strips timestamps before returning JSON to parent components
 *
 * @param json - TipTap JSONContent to filter
 * @returns JSONContent with all timestamp nodes removed
 */
const removeTimestampNodes = (json: JSONContent | undefined): JSONContent | undefined => {
  if (!json) {
    return json;
  }

  // If this is a timestamp node, exclude it
  if (json.type === TimestampNode.name) {
    return undefined;
  }

  // Recursively process content array if it exists
  if (json.content && Array.isArray(json.content)) {
    const filteredContent = json.content
      .map(removeTimestampNodes)
      .filter((item): item is JSONContent => item !== undefined);

    // Only include content property if there are items after filtering
    if (filteredContent.length > 0) {
      return {
        ...json,
        content: filteredContent,
      };
    }

    // If content is now empty, return node without content property
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { content, ...rest } = json;
    return rest;
  }

  return json;
};
